import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, CircularProgress, Alert, Box, Typography, Chip,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import CustomMonthCalendar from '../common/CustomMonthCalendar';
import { getAvailableSlots, getAvailableDays } from '../../services/expert.service';
import { appointmentService } from '../../services/appointment.service';
import { toastService } from '../../services/toast.service';
import { AvailableSlot } from '../../types/expert';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  appointmentId: number;
  expertId: number;
  expertName: string;
  /**
   * Хто переносить. C9: `clientNotes` показуємо лише клієнту — бек ігнорує це поле
   * від експерта (preserve), і UI не має його показувати.
   */
  actorRole?: 'client' | 'expert';
}

export const RescheduleAppointmentDialog: React.FC<Props> = ({
  open, onClose, onSuccess, appointmentId, expertId, expertName, actorRole = 'client',
}) => {
  const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [daysLoading, setDaysLoading] = useState(false);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [clientNotes, setClientNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchDays = useCallback(async (month: string) => {
    setDaysLoading(true);
    try {
      const days = await getAvailableDays(expertId, month);
      setAvailableDays(days);
    } catch {
      setAvailableDays([]);
    } finally {
      setDaysLoading(false);
    }
  }, [expertId]);

  const fetchSlots = useCallback(async (date: Date | null) => {
    if (!date) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    setSlots([]);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const result = await getAvailableSlots(expertId, dateStr);
      setSlots(result);
    } catch {
      setSlotsError('Не вдалося завантажити доступні слоти.');
    } finally {
      setSlotsLoading(false);
    }
  }, [expertId]);

  useEffect(() => {
    if (open) {
      const today = new Date();
      setCurrentMonth(format(today, 'yyyy-MM'));
      setSelectedDate(today);
      setAvailableDays([]);
      setSlots([]);
      setSelectedSlot(null);
      setClientNotes('');
      setSubmitError(null);
      fetchDays(format(startOfMonth(today), 'yyyy-MM'));
      fetchSlots(today);
    }
  }, [open, fetchDays, fetchSlots]);

  useEffect(() => {
    if (!open || !selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    if (availableDays.includes(dateStr)) {
      fetchSlots(selectedDate);
    } else {
      setSlots([]);
      setSelectedSlot(null);
    }
  }, [open, selectedDate, availableDays, fetchSlots]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await appointmentService.rescheduleAppointment(appointmentId, {
        scheduledAt: selectedSlot,
        // C9: бек ігнорує clientNotes від експерта; UI теж не передає
        clientNotes: actorRole === 'client' ? (clientNotes || undefined) : undefined,
      });
      toastService.success('Зустріч перенесено. Очікуйте підтвердження від експерта.');
      onSuccess();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Не вдалося перенести зустріч.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Перенести зустріч з {expertName}</DialogTitle>
      <DialogContent dividers>
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>1. Виберіть нову дату</Typography>
            <CustomMonthCalendar
              currentMonth={currentMonth}
              onMonthChange={(m) => { setCurrentMonth(m); fetchDays(m); }}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              highlightedDates={availableDays}
              disabledDates={[]}
            />
            {daysLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>2. Виберіть час</Typography>
            {slotsLoading ? (
              <CircularProgress />
            ) : slotsError ? (
              <Alert severity="error">{slotsError}</Alert>
            ) : slots.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {slots.map((slot, i) => (
                  <Chip
                    key={`${slot.startTime}-${i}`}
                    label={format(parseISO(slot.startTime), 'HH:mm')}
                    onClick={() => slot.isAvailable && setSelectedSlot(slot.startTime)}
                    disabled={!slot.isAvailable}
                    color={selectedSlot === slot.startTime ? 'primary' : 'default'}
                    variant={selectedSlot === slot.startTime ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">На вибрану дату немає вільних слотів.</Typography>
            )}
            {/* C9: поле "Нотатки для експерта" — клієнтське, не для експерта */}
            {selectedSlot && actorRole === 'client' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>3. Коментар (необов'язково)</Typography>
                <TextField
                  label="Нотатки для експерта"
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={submitting}>Скасувати</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedSlot || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Перенести'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
